<app-card
  [tone]="tone()"
  [badge]="statusLabel()"
  [portalUrl]="portalUrl()"
  [staleAge]="stale() ? age() : null"
>
  <div card-title>
    <h2>{{ app().name ?? 'Unknown application' }}</h2>
    @if (app().roleName) {
      <p class="role">
        {{ app().roleName }}
        <app-info-tip [text]="roleHelp" label="What is a role?" />
      </p>
    }
    <button
      type="button"
      class="copy-id"
      [attr.title]="'Copy the resource ID ' + app().appId"
      (click)="copyId()"
    >
      {{ copied() ? 'Copied' : 'Copy ID' }}
    </button>
  </div>
  <app-info-tip
    card-badge-extra
    [text]="statusHelp()"
    label="What does this status mean?"
    align="end"
  />

  @if (app().status === 'Error') {
    <p class="message" role="alert">{{ app().error }}</p>
  } @else if (app().status === 'AccessDenied') {
    <p class="message">{{ app().error }}</p>
  } @else if (app().totalRequests === 0) {
    <p class="message">
      @if (app().statusReason) {
        {{ app().statusReason }}
      } @else if (app().status === 'NoData') {
        No requests or outgoing calls were found.
      } @else {
        No requests or outgoing calls were found; the status is based on exceptions.
      }
    </p>
  } @else {
    @if (app().statusReason) {
      <p class="reason">{{ app().statusReason }}</p>
    }
    <ul class="channels" aria-label="Health of each kind of request or call">
      @for (channel of app().channels; track channel.name) {
        <li [class]="channel.status.toLowerCase()">
          <span class="channel-name">{{ channel.name }}</span>
          <span class="figures">
            <strong>{{ channel.percentage | number: '1.0-1' }}%</strong>
            <span class="when">{{ channel.failed }} of {{ channel.total }} failed</span>
          </span>
          <span class="bar" aria-hidden="true">
            <span class="bar-fill" [style.width.%]="channel.percentage"></span>
          </span>
        </li>
      }
    </ul>
    @if (app().clientErrorRequests > 0) {
      <p class="client-errors">
        {{ app().clientErrorRequests }} client
        {{ app().clientErrorRequests === 1 ? 'error' : 'errors' }} (4xx), not counted as failures
        <app-info-tip [text]="clientErrorHelp" label="What are client errors?" />
      </p>
    }

    <ul class="strip" aria-hidden="true">
      @for (request of app().latestRequests; track $index) {
        <li
          [class.ok]="request.outcome === 'Success'"
          [class.client]="request.outcome === 'ClientError'"
          [class.fail]="request.outcome === 'ServerError'"
        ></li>
      }
    </ul>

    <details (toggle)="onToggle($event)">
      <summary>Latest {{ app().totalRequests }} requests and calls</summary>
      @if (detailsOpen()) {
        @if (failedCount() > 0) {
          <label class="only-failures">
            <input type="checkbox" [checked]="onlyFailures()" (change)="setOnlyFailures($event)" />
            Show only failures ({{ failedCount() }})
          </label>
        }
        <ul
          class="calls"
          [attr.aria-label]="'Latest requests and calls for ' + (app().name ?? app().appId)"
        >
          @for (request of visibleRequests(); track $index) {
            <li class="call">
              <div class="call-head">
                <span
                  class="result"
                  [class.ok]="request.outcome === 'Success'"
                  [class.client]="request.outcome === 'ClientError'"
                  [class.fail]="request.outcome === 'ServerError'"
                >
                  {{ outcomeLabel(request) }} {{ request.resultCode }}
                </span>
                <span class="kind">{{ kindLabel(request) }}</span>
                <span class="when">{{ request.timestamp | date: 'MMM d, HH:mm:ss' }}</span>
                @if (request.durationMs >= 1000) {
                  <span class="slow">{{ request.durationMs / 1000 | number: '1.0-1' }} s</span>
                }
              </div>
              <div class="call-name" [attr.title]="request.name">
                @for (part of nameParts(request.name); track $index) {
                  <span>{{ part }}</span
                  ><wbr />
                }
              </div>
            </li>
          }
        </ul>
      }
    </details>
  }
  @if (app().exceptionCount > 0) {
    <section class="exceptions" aria-label="Exceptions">
      <p class="exceptions-title">
        Exceptions in the last {{ app().exceptionWindowMinutes }} minutes:
        {{ app().exceptionCount }}
        <app-info-tip [text]="exceptionsHelp()" label="What are exceptions?" />
      </p>
      <ul>
        @for (exception of app().exceptions; track exception.type) {
          <li>
            <code>{{ exception.type }}</code> ×{{ exception.count }}
            <span class="when">last {{ exception.lastSeen | date: 'MMM d, HH:mm' }}</span>
          </li>
        }
      </ul>
    </section>
  }
  <span card-meta class="checked">Checked {{ app().checkedAt | date: 'mediumTime' }}</span>
</app-card>
